import { isSettingsDeferredOwnerPath } from './settings-deferred-owner';
import {
  SETTINGS_DELEGATED_AUTHORITY,
  settingsDelegationClassification,
} from './settings-authority';
export {
  SETTINGS_DELEGATED_AUTHORITY,
  settingsDelegationClassification,
} from './settings-authority';
import { SETTINGS_DESTINATION_ROLE_AUTHORITY } from './settings-destination-authority';
import { SETTINGS_DESTINATION_PATTERNS } from './settings-route-inventory';
import { findClusterForPath } from '@/components/nav-v2/settings/settings-ia';
import { isRoleAllowedForPath } from '@/lib/route-access';
import { normalizeRole } from '@/lib/rbac';
import { isPayEventFactQueuePath } from '@/lib/payroll/pay-event-fact-integrity';
import { resolveBaseSettingsAllowedRoles } from './settings-base-access';
import { mainOwnerSettingsAllowedRoles } from './settings-owner-main-parity';
import {
  isOwnNotificationPreferencePath,
  SETTINGS_TENANT_ADMIN_ROLES,
} from './settings-access-roles';
import type { SettingsDelegationPreference } from './settings-delegation';
import { isSettingsLeafHidden } from './settings-visibility';

export const SETTINGS_HANDOFF_MESSAGE = 'Ask your organisation administrator';
const destinationExpressions = SETTINGS_DESTINATION_PATTERNS.map(
  (pattern) =>
    new RegExp(
      '^' +
        pattern
          .split('/')
          .map((segment) =>
            segment.startsWith('[[...')
              ? '(?:/.*)?'
              : segment.startsWith('[...')
                ? '/.+'
                : segment.startsWith('[')
                  ? '/[^/]+'
                  : segment
                    ? '/' + segment
                    : '',
          )
          .join('') +
        '/?$',
    ),
);

export type SettingsAccessContext = {
  /** Additional pre-existing destination/layout/entitlement guards; false always refuses. */
  destinationAllowed?: boolean;
  entitlementAllowed?: boolean;
  navigationAllowed?: boolean;
  hiddenHrefs?: readonly string[];
  billingPlan?: string | null;
  /** Only independently verified facts supplied by the caller; absent facts refuse controls. */
  controlFacts?: Readonly<
    Record<
      string,
      {
        actionAllowed: boolean;
        databaseAllowed: boolean;
        resourceAllowed: boolean;
      }
    >
  >;
};

/** Persisted Settings preferences never narrow these roles, so they skip the read. */
export function isSettingsAdministratorRole(role: string | null | undefined): boolean {
  return (SETTINGS_TENANT_ADMIN_ROLES as readonly string[]).includes(normalizeRole(role) ?? '');
}

/**
 * Persisted tenant narrowing only: an explicit cluster denial or a hidden leaf.
 * Server-command paths use this so a role main admitted to the command (not the
 * page) keeps it unless the organisation has restricted it.
 */
export function settingsPersistedNarrowing({
  leafId,
  role: rawRole,
  delegation,
  hiddenHrefs = [],
}: {
  leafId: string;
  role: string | null | undefined;
  delegation?: SettingsDelegationPreference | null;
  hiddenHrefs?: readonly string[];
}): { denied: boolean; hidden: boolean } {
  const pathname = leafId.split(/[?#]/, 1)[0] ?? '';
  if (isSettingsDeferredOwnerPath(pathname)) delegation = null;
  const role = normalizeRole(rawRole);
  const match = findClusterForPath(leafId.split('?')[0] ?? leafId);
  const clusterId = match?.item.delegationClusterId ?? match?.cluster.id;
  const personal = isOwnNotificationPreferencePath(pathname);
  const exception =
    personal || isPayEventFactQueuePath(pathname) || pathname.startsWith('/settings/compliance');
  const admin = Boolean(role && (SETTINGS_TENANT_ADMIN_ROLES as readonly string[]).includes(role));
  return {
    denied: Boolean(
      !exception &&
      !admin &&
      role &&
      clusterId &&
      delegation?.deniedRolesByCluster[clusterId]?.includes(role),
    ),
    hidden: Boolean(
      !personal &&
      !isPayEventFactQueuePath(pathname) &&
      !admin &&
      match &&
      isSettingsLeafHidden(match.item.href, hiddenHrefs),
    ),
  };
}

/** Client/server safe authority intersection. No auth, I/O, privilege, or Server Action exports. */
export function resolveSettingsAccess({
  leafId,
  actor,
  delegation,
  context = {},
}: {
  leafId: string;
  actor: { role: string | null | undefined };
  delegation?: SettingsDelegationPreference | null;
  context?: SettingsAccessContext;
}): { visible: boolean; canOpen: boolean; controls: Record<string, 'allowed' | 'handoff'> } {
  const pathname = leafId.split(/[?#]/, 1)[0] ?? '';
  // Owner destinations keep main's own delegation semantics in every persisted
  // state: main's delegated role list replaces the base cohort, and the
  // release 1 denial/grant paths below are switched off for them.
  const owner = isSettingsDeferredOwnerPath(pathname);
  const ownerAllowedRoles = owner ? mainOwnerSettingsAllowedRoles(pathname, delegation) : null;
  if (owner) delegation = null;
  const role = normalizeRole(actor.role);
  const match = findClusterForPath(leafId.split('?')[0] ?? leafId);
  const destinationIndex = destinationExpressions.findIndex((expression) =>
    expression.test(pathname),
  );
  const destinationPattern = SETTINGS_DESTINATION_PATTERNS[destinationIndex];
  const destinationRoles =
    destinationPattern && SETTINGS_DESTINATION_ROLE_AUTHORITY[destinationPattern];
  const staticDestinationAllowed =
    !destinationRoles || Boolean(role && destinationRoles.includes(role));
  const known =
    destinationIndex >= 0 ||
    Boolean(
      match && !pathname.startsWith('/settings') && pathname === match.item.href.split('#')[0],
    );
  const clusterId = match?.item.delegationClusterId ?? match?.cluster.id;
  const personal = isOwnNotificationPreferencePath(pathname);
  const admin = Boolean(role && (SETTINGS_TENANT_ADMIN_ROLES as readonly string[]).includes(role));
  const { denied, hidden } = settingsPersistedNarrowing({
    leafId,
    role,
    delegation,
    hiddenHrefs: context.hiddenHrefs,
  });
  const granted = Boolean(
    !denied && role && clusterId && delegation?.grantedRolesByCluster[clusterId]?.includes(role),
  );
  const endToEndGrant = Boolean(
    granted && role && settingsDelegationClassification(pathname, role) === 'E',
  );
  const baseAllowed = Boolean(
    role && (ownerAllowedRoles ?? resolveBaseSettingsAllowedRoles(pathname)).includes(role),
  );
  // A reviewed E grant reconciles the duplicated Settings route cohort only.
  // Other destination, module, action, resource, RPC and RLS guards still intersect.
  const routeAllowed = Boolean(
    role && isRoleAllowedForPath(pathname, role, context.billingPlan, delegation),
  );
  const canOpen =
    known &&
    Boolean(role) &&
    !denied &&
    !hidden &&
    (baseAllowed || endToEndGrant) &&
    routeAllowed &&
    staticDestinationAllowed &&
    context.destinationAllowed !== false &&
    context.entitlementAllowed !== false;
  const controls: Record<string, 'allowed' | 'handoff'> = {};
  for (const [id, control] of Object.entries(
    SETTINGS_DELEGATED_AUTHORITY[destinationPattern ?? pathname]?.controls ?? {},
  )) {
    const facts = context.controlFacts?.[id];
    const commandAdmitted =
      canOpen ||
      (control.pageIndependentCommand === true &&
        known &&
        Boolean(role) &&
        !denied &&
        !hidden &&
        context.entitlementAllowed !== false);
    controls[id] =
      commandAdmitted &&
      Boolean(role && control.roles.includes(role)) &&
      facts?.actionAllowed === true &&
      facts.databaseAllowed === true &&
      facts.resourceAllowed === true
        ? 'allowed'
        : 'handoff';
  }
  // Personal destinations stay directly reachable even when omitted from the
  // Settings rail. Preserve the old rail omissions; no denial grants authority.
  const personalNavDenied = Boolean(
    personal &&
    !admin &&
    role &&
    clusterId &&
    delegation?.deniedRolesByCluster[clusterId]?.includes(role),
  );
  const navHidden = Boolean(
    match && isSettingsLeafHidden(match.item.href, context.hiddenHrefs ?? []),
  );
  return {
    canOpen,
    visible: canOpen && !personalNavDenied && !navHidden && context.navigationAllowed !== false,
    controls,
  };
}

export function isSettingsControlAllowed(
  access: ReturnType<typeof resolveSettingsAccess>,
  controlId: string,
): boolean {
  return access.controls[controlId] === 'allowed';
}
